<template>
  <div class="frames-editor">
    <div class="frames-editor__canvas">
      <Flatfinder
        v-if="currentImage"
        :id="id"
        :image="currentImage"
        :value="currentItems"
        :actions="mapActions"
        @input="setItems"
      />
      <div v-else class="frames-editor__empty">
        <Icon :icon="['fal', 'image']" size="2x" />
      </div>

      <template v-if="currentImage && frames.length > 1">
        <button
          type="button"
          class="frames-editor__nav frames-editor__nav--prev"
          v-tooltip="$t('prev')"
          @click="step(-1)"
        >
          <Icon :icon="['fal', 'angle-left']" />
        </button>
        <button
          type="button"
          class="frames-editor__nav frames-editor__nav--next"
          v-tooltip="$t('next')"
          @click="step(1)"
        >
          <Icon :icon="['fal', 'angle-right']" />
        </button>
        <span class="frames-editor__counter">{{ current + 1 }}/{{ frames.length }}</span>
      </template>
    </div>

    <CardinalDirectionDialog
      :show="directionOpen"
      :frames="frames"
      :current="current"
      @update:current="$emit('update:current', $event)"
      @close="directionOpen = false"
    />

    <SunlightDialog
      :show="sunlightOpen"
      :frames="frames"
      :current="current"
      @update:current="$emit('update:current', $event)"
      @close="sunlightOpen = false"
    />

    <ImportMarkingsDialog
      :show="importOpen"
      :frames="frames"
      :current="current"
      :project="project"
      @close="importOpen = false"
    />
  </div>
</template>

<script>
import Flatfinder from '@/components/Flatfinder'
import SunlightDialog from './SunlightDialog'
import CardinalDirectionDialog from './CardinalDirectionDialog'
import ImportMarkingsDialog from './ImportMarkingsDialog'

export default {
  props: {
    id: String,
    project: String,
    frames: {
      type: Array,
      default: () => [],
    },
    current: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      directionOpen: false,
      sunlightOpen: false,
      importOpen: false,
    }
  },
  computed: {
    currentFrame() {
      return this.frames[this.current]
    },
    currentImage() {
      return this.$path('image.url', this.currentFrame)
    },
    currentItems() {
      return (this.currentFrame && this.currentFrame.items) || []
    },
    previousItems() {
      const total = this.frames.length
      if (total < 2) return []
      const previous = this.frames[(this.current - 1 + total) % total]
      return (previous && previous.items) || []
    },
    canCopyItems() {
      return !this.currentItems.length && this.previousItems.length > 0
    },
    mapActions() {
      return [
        {
          icon: ['fal', 'clone'],
          label: this.$t('copySelectionsFromPreviousFrame'),
          action: this.copyItemsFromPrevious,
          condition: this.canCopyItems,
        },
        { icon: 'compass', label: this.$t('cardinalDirection'), action: this.openDirection },
        {
          icon: ['fal', 'sun'],
          label: this.$t('sunlightConditions'),
          action: this.openSunlight,
        },
        {
          icon: ['fal', 'file-upload'],
          tooltip: this.$t('importMarkingFiles'),
          action: this.openImport,
        },
      ].filter(item => !('condition' in item) || item.condition)
    },
  },
  methods: {
    setItems(items) {
      if (!this.currentFrame) return
      this.$set(this.currentFrame, 'items', items)
    },
    copyItemsFromPrevious() {
      this.$set(this.currentFrame, 'items', JSON.parse(JSON.stringify(this.previousItems)))
    },
    openDirection() {
      this.directionOpen = true
    },
    openSunlight() {
      this.sunlightOpen = true
    },
    openImport() {
      this.importOpen = true
    },
    step(direction) {
      const total = this.frames.length
      this.$emit('update:current', (this.current + direction + total) % total)
    },
  },
  components: {
    Flatfinder,
    SunlightDialog,
    CardinalDirectionDialog,
    ImportMarkingsDialog,
  },
}
</script>

<style lang="scss">
.frames-editor {
  &__canvas {
    position: relative;
    background-color: white;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-md);
    padding: 1rem;

    .leaflet__map {
      background-color: transparent;
    }
  }

  &__nav,
  &__counter {
    position: absolute;
    z-index: 1001;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background-color: rgba(0, 0, 0, 0.55);
    color: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  }

  &__nav {
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 150ms;

    &:hover {
      background-color: rgba(0, 0, 0, 0.75);
    }

    &--prev {
      left: 1.5rem;
    }

    &--next {
      right: 1.5rem;
    }
  }

  &__counter {
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.75em;
    pointer-events: none;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 4rem 1rem;
    color: var(--grey);
  }
}
</style>
